import { useState, useRef, type ReactNode } from 'react'
import { Info } from 'lucide-react'

interface TooltipProps {
  content: string
  heading?: string
  children: ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  /** Only show when the trigger content is actually truncated. */
  onlyWhenTruncated?: boolean
}

export function Tooltip({ content, heading, children, position = 'top', onlyWhenTruncated }: TooltipProps) {
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)

  const show = () => {
    if (onlyWhenTruncated) {
      const el = triggerRef.current?.firstElementChild as HTMLElement | undefined
      if (el && el.scrollWidth <= el.offsetWidth) return
    }
    timer.current = setTimeout(() => setOpen(true), 200)
  }
  const hide = () => {
    if (timer.current) clearTimeout(timer.current)
    setOpen(false)
  }

  const offset: Record<string, React.CSSProperties> = {
    top: { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8 },
    bottom: { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 8 },
    left: { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: 8 },
    right: { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: 8 },
  }

  return (
    <span ref={triggerRef} className="relative inline-flex" onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {open && (
        <span
          role="tooltip"
          className="absolute flex flex-col"
          style={{
            ...offset[position], zIndex: 50, backgroundColor: '#2D3036', border: '0.5px solid #4B5563',
            padding: 8, gap: 10, maxWidth: 240, width: 'max-content',
          }}
        >
          {heading && (
            <span className="font-mulish font-bold text-white" style={{ fontSize: 12, letterSpacing: '-0.12px' }}>{heading}</span>
          )}
          <span className="font-mulish text-white" style={{ fontSize: 12, letterSpacing: '-0.12px', lineHeight: '16px' }}>
            {content}
          </span>
        </span>
      )}
    </span>
  )
}

/** The documented Info icon pattern: label followed by a 14px info icon carrying a tooltip. */
export function InfoHint({ content }: { content: string }) {
  return (
    <Tooltip content={content}>
      <Info size={14} className="text-text-support cursor-pointer" />
    </Tooltip>
  )
}
